import ActionTextIcon from "@/components/icons/ActionTextIcon";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleNettoPoints } from "@/utils/formatUtils";
import { tInterval } from "@/utils/translationUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Descriptions, Popover, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { useFilterCommissionsBatchOutputs } from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { AgentTypeTagWithName } from "../../../../agent/components/tags/AgentTypeTagWithName";
import { formatAgentIdNumber } from "../../../../agent/utils";
import { useHasPermissions, useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { PaidInBatchField } from "../../../outputs/components/PaidInBatchField";
import { CommissionsBatchStep } from "../../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../paths";
import { CommissionsBatch, CommissionsBatchOutputFilterPageRequest, CommissionsOutput } from "../../types";
import { showPaidCommissionsPopover } from "../../utils";
import { PaidCommissionDetailTable } from "./PaidCommissionDetailTable";

interface Props {
  batch: CommissionsBatch;
  filterData: CommissionsBatchOutputFilterPageRequest;
  onChangeFilter: (filter: CommissionsBatchOutputFilterPageRequest) => void;
}

export const CommissionsBatchOutputsTable = ({ batch, filterData, onChangeFilter }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const hasReadAgentPermission = useHasPermissions(Permission.AGENT_READ);

  const { data, isFetching } = useFilterCommissionsBatchOutputs({
    id: batch.id,
    object: filterData,
  });

  const columns: ColumnsType<CommissionsOutput> = useMemo(
    () => [
      {
        key: "gainer",
        title: t("commissions.batch.attrs.attachment.gainer"),
        width: 195,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <AgentTypeTagWithName
            agent={record.gainer}
            ellipsis
            showLink={hasReadAgentPermission}
            showStatuses
            hideIdNumber
            showRepresentatives
            linkTarget="_blank"
          />
        ),
      },
      {
        key: "idNumber",
        title: t("agent.attrs.idNumber"),
        width: 80,
        render: (_, record) => formatAgentIdNumber(record.gainer),
      },
      {
        key: "calculatedCommissionAmount",
        title: t("commissions.batch.attrs.attachment.calculatedCommissionAmount"),
        align: "right",
        width: 150,
        render: (_, record) => formatLocaleCurrency(record.calculatedCommissionAmount, isTopAgentPlus ? 6 : 2),
      },
      {
        key: "nettoPointAmount",
        title: t("commissions.batch.helpers.nettoPointAmount"),
        align: "right",
        width: 150,
        render: (_, record) => (
          <Popover
            content={
              <Descriptions
                column={1}
                size="small"
                bordered
                styles={{ content: { textAlign: "right" } }}
                items={[
                  {
                    key: "individualPointAmount",
                    label: t("commissions.batch.attrs.attachment.individualPointAmount"),
                    children: formatLocaleNettoPoints(record.individualPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                  {
                    key: "groupPointAmount",
                    label: t("commissions.batch.attrs.attachment.groupPointAmount"),
                    children: formatLocaleNettoPoints(record.groupPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                  {
                    key: "totalPointAmount",
                    label: t("commissions.batch.attrs.attachment.totalPointAmount"),
                    children: formatLocaleNettoPoints(record.totalPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                ]}
              />
            }
          >
            {formatLocaleNettoPoints(record.totalPointAmount, isTopAgentPlus ? 6 : 2)}
          </Popover>
        ),
      },
      {
        key: "specialCommissionAmount",
        title: t("commissions.batch.attrs.attachment.specialCommissionAmount"),
        align: "right",
        width: 130,
        render: (_, record) => (
          <Popover
            content={
              <>
                {record.specialCommissionCount > 0 && record.excludedSpecialCommissionCount > 0 ? (
                  <>
                    {tInterval(
                      "commissions.batch.helpers.includedSpecialCommissionCountTooltip_interval",
                      record.specialCommissionCount,
                    )}
                    <br />
                    {tInterval(
                      "commissions.batch.helpers.excludedSpecialCommissionCountTooltip_interval",
                      record.excludedSpecialCommissionCount,
                    )}
                  </>
                ) : record.specialCommissionCount > 0 ? (
                  tInterval(
                    "commissions.batch.helpers.includedSpecialCommissionCountTooltip_interval",
                    record.specialCommissionCount,
                  )
                ) : record.excludedSpecialCommissionCount > 0 ? (
                  tInterval(
                    "commissions.batch.helpers.excludedSpecialCommissionCountTooltip_interval",
                    record.excludedSpecialCommissionCount,
                  )
                ) : (
                  t("commissions.batch.helpers.noSpecialCommissions")
                )}
              </>
            }
          >
            {!batch.stepChangeInProgress &&
            (record.specialCommissionCount > 0 || record.excludedSpecialCommissionCount > 0) ? (
              <span>
                <ActionTextIcon
                  text={formatLocaleCurrency(record.specialCommissionAmount)}
                  path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.specialCommissions.to, {
                    id1: batch.id,
                    id2: record.gainer?.id,
                  })}
                  color="blue"
                />
              </span>
            ) : (
              formatLocaleCurrency(record.specialCommissionAmount)
            )}
          </Popover>
        ),
      },
      {
        key: "bailAccount",
        title: t("commissions.batch.attrs.attachment.bailAccount"),
        children: [
          {
            key: "bailAccountOpeningBalance",
            title: t("commissions.batch.attrs.attachment.bailAccountOpeningBalance"),
            align: "right",
            width: 120,
            render: (_, record) =>
              isDefined(record.bailAccountOpeningBalance)
                ? formatLocaleCurrency(record.bailAccountOpeningBalance)
                : undefined,
          },
          {
            key: "bailAccountAutomaticMovement",
            title: t("commissions.batch.attrs.attachment.bailAccountAutomaticMovement"),
            align: "right",
            width: 120,
            render: (_, record) =>
              isDefined(record.bailAccountAutomaticMovement)
                ? formatLocaleCurrency(record.bailAccountAutomaticMovement)
                : undefined,
          },
          {
            key: "bailAccountManualMovement",
            title: t("commissions.batch.attrs.attachment.bailAccountManualMovement"),
            align: "right",
            width: 120,
            render: (_, record) =>
              isDefined(record.bailAccountManualMovement)
                ? formatLocaleCurrency(record.bailAccountManualMovement)
                : undefined,
          },
          {
            key: "bailAccountClosingBalance",
            title: t("commissions.batch.attrs.attachment.bailAccountClosingBalance"),
            align: "right",
            width: 120,
            render: (_, record) =>
              isDefined(record.bailAccountClosingBalance)
                ? formatLocaleCurrency(record.bailAccountClosingBalance)
                : undefined,
          },
        ],
      },
      {
        key: "debtAccount",
        title: t("commissions.batch.attrs.attachment.debtAccount"),
        children: [
          {
            key: "debtAccountOpeningBalance",
            title: t("commissions.batch.attrs.attachment.debtAccountOpeningBalance"),
            align: "right",
            width: 120,
            render: (_, record) => formatLocaleCurrency(record.debtAccountOpeningBalance),
          },
          {
            key: "debtAccountAutomaticMovement",
            title: t("commissions.batch.attrs.attachment.debtAccountAutomaticMovement"),
            align: "right",
            width: 120,
            render: (_, record) => formatLocaleCurrency(record.debtAccountAutomaticMovement),
          },
          {
            key: "debtAccountManualMovement",
            title: t("commissions.batch.attrs.attachment.debtAccountManualMovement"),
            align: "right",
            width: 120,
            render: (_, record) => formatLocaleCurrency(record.debtAccountManualMovement),
          },
          {
            key: "debtAccountClosingBalance",
            title: t("commissions.batch.attrs.attachment.debtAccountClosingBalance"),
            align: "right",
            width: 120,
            render: (_, record) => formatLocaleCurrency(record.debtAccountClosingBalance),
          },
        ],
      },
      {
        key: "paidCommissionAmount",
        title: t("commissions.batch.attrs.attachment.paidCommissionAmount"),
        align: "right",
        width: 130,
        render: (_, record) => (
          <Popover
            content={showPaidCommissionsPopover(record) ? <PaidCommissionDetailTable data={record} /> : undefined}
          >
            <b>
              {showPaidCommissionsPopover(record)
                ? formatLocaleCurrency(
                    numberOrZero(record.calculatedCommissionAmountWithNoVat) +
                      numberOrZero(record.calculatedCommissionAmountWithoutVat),
                  )
                : formatLocaleCurrency(record.paidCommissionAmount)}
            </b>
          </Popover>
        ),
      },
      {
        key: "paidInBatch",
        align: "center",
        fixed: "right",
        width: 40,
        render: (_, record) => (
          <PaidInBatchField
            noAction={batch.step === CommissionsBatchStep.RECAPITULATION}
            data={{
              paidInBatch: record.paidInBatch,
              id: record.id,
              optimisticLockVersion: record.optimisticLockVersion,
            }}
          />
        ),
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 115,
        render: (_, record) => (
          <ActionTextIcon
            icon="eye"
            color="blue"
            text={t("commissions.batch.actions.openCommissionsOutput")}
            path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.output.to, {
              id1: batch.id,
              id2: record.id,
            })}
            target="_blank"
            disabled={
              batch.stepChangeInProgress ||
              (batch.step === CommissionsBatchStep.FINISH && !record.file) ||
              !record.batch
            }
          />
        ),
      },
    ],
    [hasReadAgentPermission, batch, isTopAgentPlus],
  );

  return (
    <Table<CommissionsOutput>
      {...tableStandardProps()}
      columns={columns}
      loading={isFetching}
      scroll={{ x: 1950 }}
      dataSource={data?.pageData}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => onChangeFilter({ ...filterData, pageIndex: pageIndex - 1 }),
      }}
    />
  );
};
